import React, {FC, ReactNode, useCallback, useMemo, useState} from 'react';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {LinkedConversationsDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/linkedConversationsDataEventObject';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {BulkActionsTrackingTypesEnum} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {
  AsyncSearchDropdown,
  AsyncSearchDropdownDataFetcher,
} from '../../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {TypedRowValue} from '../../../../../../libs/shared/ui/src/components/dropdowns/source/dropdownSource';
import {useAppDispatch} from '../../../../../helpers/appReduxHelpers';
import {LinkedConversationsError} from '../../../../../helpers/models/linkedConversationsHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {
  bulkLinkConversationsAsyncAction,
  fetchRecentlyViewedAsyncAction,
  searchLinkableConversationsAsyncAction,
} from '../../../../../store/data/conversations/linkedConversations/linkedConversationsActions';
import {
  InboxesParameters,
  RInboxesConversationsParameters,
  RInboxesTeammateMessagesParameters,
} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {navigateToConversation} from '../../../../../store/stateless/navigationActions';
import {LinkConversationsDropdownRow} from './linkConversationsDropdownRow';

/*
 * Types.
 */

enum LinkConversationsDropdownItemsEnum {
  CATEGORY = 'CATEGORY',
  CONVERSATION = 'CONVERSATION',
}

type LinkConversationsDropdownRowTypedValue =
  | TypedRowValue<LinkConversationsDropdownItemsEnum.CATEGORY, ReactNode>
  | TypedRowValue<LinkConversationsDropdownItemsEnum.CONVERSATION, ConversationBaseModel>;

/*
 * Props.
 */

interface LinkConversationsProps extends DropdownProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters?: InboxesParameters;
  isBulkActionRequired: boolean;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
  selectedConversations: ReadonlyArray<ConversationBaseModel>;
  onBackButtonClick?: () => void;
  onLinkConversationsError: (error: LinkedConversationsError) => void;
}

/*
 * Constants.
 */

const recentlyViewedCategory: LinkConversationsDropdownRowTypedValue = {
  type: LinkConversationsDropdownItemsEnum.CATEGORY,
  value: <Trans id="link-conversations-dropdown-recently-viewed">Recently viewed</Trans>,
};

/*
 * Component.
 */

export const LinkConversationsDropdown: FC<LinkConversationsProps> = (props) => {
  const {selectedConversations} = props;
  const {trackDataEvent} = useDataEventTracking();
  const localize = useLocalize();

  const [query, setQuery] = useState('');
  const dispatch = useAppDispatch();

  const selectedConversationItems: ReadonlyArray<LinkConversationsDropdownRowTypedValue> = useMemo(() => {
    if (selectedConversations.length === 1) {
      return [];
    }
    return selectedConversations.map((conversation) => ({
      type: LinkConversationsDropdownItemsEnum.CONVERSATION,
      value: conversation,
    }));
  }, [selectedConversations]);

  const onRequestRecentlyViewed: AsyncSearchDropdownDataFetcher<LinkConversationsDropdownRowTypedValue> =
    useCallback(
      async (_searchQuery, _items, cancelToken, _paginationData) => {
        const response = await dispatch(fetchRecentlyViewedAsyncAction(cancelToken));
        cancelToken.throwIfRequested();

        const selectedConversationIds = selectedConversations.map((conversation) => conversation.id);

        const recentlyViewedItems: ReadonlyArray<LinkConversationsDropdownRowTypedValue> = response
          .filter((conversation) => !selectedConversationIds.includes(conversation.id))
          .map((conversation) => ({
            type: LinkConversationsDropdownItemsEnum.CONVERSATION,
            value: conversation,
          }));

        return {
          data: [...selectedConversationItems, recentlyViewedCategory, ...recentlyViewedItems],
          canRequestMore: false,
        };
      },
      [dispatch, selectedConversationItems, selectedConversations],
    );

  const onSearchFetch: AsyncSearchDropdownDataFetcher<LinkConversationsDropdownRowTypedValue> = useCallback(
    async (searchQuery, items, cancelToken, paginationData) => {
      if (
        !props.inboxesParameters ||
        !(
          RInboxesConversationsParameters.is(props.inboxesParameters) ||
          RInboxesTeammateMessagesParameters.is(props.inboxesParameters)
        )
      ) {
        return {
          data: [],
          canRequestMore: false,
        };
      }

      const conversationSearchResults = await dispatch(
        searchLinkableConversationsAsyncAction(props.inboxesParameters, searchQuery, cancelToken),
      );
      cancelToken.throwIfRequested();

      const searchItems: ReadonlyArray<LinkConversationsDropdownRowTypedValue> = conversationSearchResults
        ? conversationSearchResults.map((conversation) => ({
            type: LinkConversationsDropdownItemsEnum.CONVERSATION,
            value: conversation,
          }))
        : [];

      return {
        data: searchItems,
        canRequestMore: false,
      };
    },
    [query],
  );

  const onOriginalConversationSelect = async (originalConversationId: number) => {
    const selectedConversationIds = selectedConversations
      .map((c) => c.id)
      .filter((id) => id !== originalConversationId);
    try {
      await dispatch(
        bulkLinkConversationsAsyncAction(originalConversationId, selectedConversationIds, localize),
      );
      if (props.selectedConversations.length > 1) {
        await dispatch(navigateToConversation(originalConversationId));
      }
      trackDataEvent(
        DataEventActionsEnum.SUCCESS,
        LinkedConversationsDataEventObject.BULK_LINK_CONVERSATION,
        {
          sourceConversationIds: selectedConversationIds,
          targetConversationId: originalConversationId,
          isSearchClick: query.length > 0,
        },
      );
    } catch (error) {
      if (error instanceof LinkedConversationsError) {
        props.onLinkConversationsError(error);
      }
    }
  };

  const renderRow = (item: LinkConversationsDropdownRowTypedValue, index: number) => {
    const {type, value} = item;
    if (type === LinkConversationsDropdownItemsEnum.CONVERSATION) {
      return (
        <LinkConversationsDropdownRow
          panelId={props.panelId}
          onOriginalConversationSelect={onOriginalConversationSelect}
          conversation={value}
          index={index}
        />
      );
    }

    return <DropdownCategory>{value}</DropdownCategory>;
  };

  return (
    <AsyncSearchDropdown
      {...props}
      query={query}
      onQueryChange={setQuery}
      headerTitle={<Trans id="link-conversations-dropdown-header">Select original conversation</Trans>}
      headerBackButtonLabel={<Trans id="link-conversations-dropdown-header-back">Back</Trans>}
      onHeaderBackButtonClick={props.onBackButtonClick}
      placement="top-end"
      renderRow={renderRow}
      onRequestData={query ? onSearchFetch : onRequestRecentlyViewed}
      maxHeight={430}
      width={300}
      findRowHeight={(item) => {
        if (item.type === LinkConversationsDropdownItemsEnum.CATEGORY) {
          return 30;
        }

        return 48;
      }}
    />
  );
};
